const ENERGY_INDUSTRIES = ['wind-energy', 'solar-energy', 'oil-gas', 'hydro'];

export default function Footnotes({ industry = 'construction' }) {
  const sports = industry === 'sports';
  const sportsHs = industry === 'sports-hs';
  const agriculture = industry === 'agriculture';
  const isEnergy = ENERGY_INDUSTRIES.includes(industry);

  return (
    <div className="footnotes">
      <h4>Method and limits</h4>
      <p>
        <strong>Wet bulb</strong> is computed from air temperature and relative humidity using
        Stull&rsquo;s (2011) empirical solution, accurate to roughly &plusmn;0.65&nbsp;&deg;C over
        &minus;20 to 50&nbsp;&deg;C and 5&ndash;99% RH at sea-level pressure. <strong>WBGT</strong>{' '}
        is an <em>estimate</em>: globe temperature comes from the Hunter &amp; Minyard (1999)
        regression on solar radiation, and the psychrometric wet bulb stands in for the natural wet
        bulb. It is a screening figure, not a substitute for an on-site WBGT meter.
      </p>
      {sports ? (
        <p>
          The NCAA heat rule triggers on <strong>WBGT</strong>, measured on site with a shielded
          WBGT meter at the activity area &mdash; not on a gridded forecast. Readings here are
          modelled at each ZIP&rsquo;s centroid and will differ from a meter on your field,
          sometimes substantially in shade, on turf vs. grass, or near the coast. Use this to plan
          and prioritise; take the compliance measurement on site. The cold-weather policy below
          triggers on plain <strong>air temperature</strong> instead, same as the rest of this
          dashboard.
        </p>
      ) : sportsHs ? (
        <p>
          The high school heat rule triggers on whichever metric each state association specifies
          &mdash; <strong>WBGT</strong> for most states, <strong>heat index</strong> for Indiana and
          Michigan &mdash; measured on site at the activity area, not on a gridded forecast. Readings
          here are modelled at each ZIP&rsquo;s centroid and will differ from an on-site reading,
          sometimes substantially in shade, on turf vs. grass, or near the coast. Fifteen states leave
          the cancellation call to local school district discretion rather than setting a fixed
          statewide number &mdash; check each ZIP&rsquo;s detail panel for its governing body, and
          consult your own district&rsquo;s policy for the actual trigger where one applies. The
          cold-weather policy below triggers on plain <strong>air temperature</strong> instead, and
          is a regional estimate rather than a per-state number &mdash; see Sources below.
        </p>
      ) : (
        <p>
          Where a state&rsquo;s heat rule triggers on <strong>dry bulb temperature</strong> (CA,
          WA) rather than heat index (OR, MD) or a hazard-analysis process (NV), it&rsquo;s
          typically measured in full sun at the work area with a shielded sensor &mdash; not on a
          gridded forecast. Readings here are modelled at each ZIP&rsquo;s centroid and will differ
          from a thermometer on your site, sometimes substantially in{' '}
          {agriculture ? 'open fields, near tree lines, or' : 'canyons, on unshaded slab, or'} near
          the coast. Use this to plan and prioritise; take the compliance measurement on site.
        </p>
      )}
      {!sports && !sportsHs && (
        <p>
          <strong>Wind chill</strong> (cold-weather rule below) is the National Weather Service&rsquo;s
          2001 Wind Chill Temperature Index, computed from air temperature and wind speed. NWS
          defines it only for air temperature at or below 50&deg;F and wind speed at or above 3 mph
          &mdash; outside that range there&rsquo;s no meaningful chill effect, so this dashboard shows
          plain air temperature instead of extrapolating the formula past where NWS validated it.
        </p>
      )}
      <p>
        Air quality is modelled (CAMS via Open-Meteo). For your own wildfire smoke determination,
        check a source the applicable standard names: EPA AirNow{sports || sportsHs ? '' : ', your state air quality agency, or CARB for California'}.
      </p>

      <h4 style={{ marginTop: 14 }}>Sources</h4>
      <p>
        Weather and air quality:{' '}
        <a href="https://open-meteo.com/" target="_blank" rel="noreferrer">
          Open-Meteo
        </a>{' '}
. ZIP-to-county mapping and centroids:{' '}
        <a href="https://www.census.gov/geographies/reference-files/time-series/geo/gazetteer-files.html" target="_blank" rel="noreferrer">
          US Census Bureau Gazetteer files
        </a>
        .
      </p>

      {!sports && !sportsHs && (
        <p>
          Cold weather: no state has published its own cold-stress or wind-chill standard for
          construction, agriculture, or any of the four energy sectors below, so this runs on
          the federal General Duty Clause,{' '}
          <a href="https://www.osha.gov/laws-regs/oshact/section5-duties" target="_blank" rel="noreferrer">
            29 USC &sect;654(a)(1)
          </a>
          , scored against the frostbite-time bands on the National Weather Service&rsquo;s{' '}
          <a href="https://www.weather.gov/safety/cold-wind-chill-chart" target="_blank" rel="noreferrer">
            Wind Chill Chart
          </a>{' '}
          &mdash; the same chart{' '}
          <a href="https://www.osha.gov/emergency-preparedness/guides/cold-stress" target="_blank" rel="noreferrer">
            OSHA&rsquo;s own Cold Stress Guide
          </a>{' '}
          points employers to, rather than a number OSHA publishes itself: frostbite possible in 30
          minutes at a wind chill of &minus;19&deg;F to &minus;34&deg;F, in 10 minutes at
          &minus;35&deg;F to &minus;59&deg;F, and in 5 minutes or less at &minus;60&deg;F and below.
        </p>
      )}

      {sports && (
        <p>
          Heat: the NCAA&rsquo;s endorsed NATA regional WBGT zone framework, as supplied directly
          by the user (2026-08-06) &mdash; two WBGT thresholds per zone (an alert/caution level and
          a termination/cancel level), assigned per state by geographic zone (hotter-climate states
          use higher thresholds for the same action, since athletes there are assumed more
          heat-acclimatized). The zone assignment itself reuses this dashboard&rsquo;s existing
          50-state regional grouping (previously sourced from Grundstein et al.,{' '}
          <em>Applied Geography</em>, 2015, and cross-checked against the ACHA&rsquo;s published
          copy of that table) rather than a newly-supplied state list &mdash; the new termination
          numbers line up almost exactly with that table&rsquo;s most extreme tier for two of the
          three zones, strong evidence it&rsquo;s the same underlying regional research republished
          in a simpler two-threshold form. Hawaii and DC aren&rsquo;t covered by the source study
          and default to the most protective zone. Lightning: the NCAA&rsquo;s own policy &mdash;
          suspend and evacuate the moment lightning is detected within an 8-mile radius, resuming
          only after 30 consecutive minutes with no further strikes inside that radius (any new
          strike resets the clock). This app has no live strike-distance telemetry, so any
          indicated thunderstorm activity is treated as inside the radius; confirm the actual
          distance with on-site detection equipment. Cold weather: NCAA guidelines &mdash; outdoor
          field sports generally canceled below 32&deg;F, cold-weather gear required below 25&deg;F,
          and outdoor practice terminated or moved indoors at 0&deg;F and below. Wildfire smoke: EPA
          AQI categories, per the{' '}
          <a
            href="https://assets.nfhs.org/umbraco/media/7212236/nfhs-position-statement-on-air-quality-april-2023-final.pdf"
            target="_blank"
            rel="noreferrer"
          >
            NFHS Position Statement on Air Quality
          </a>{' '}
          (2023), which points to EPA AirNow directly rather than publishing its own numeric
          tiers.
        </p>
      )}

      {sportsHs && (
        <p>
          Heat: each state&rsquo;s own high school athletic/activity association policy &mdash;
          governing body, primary metric, monitoring status, and outdoor-practice cancellation
          threshold as published by that association, encoded per state rather than re-derived from
          a single regional model. Fifteen states (Alaska, Colorado, Hawaii, Idaho, Iowa, Kansas,
          Montana, Nebraska, New Mexico, North Dakota, Pennsylvania, South Dakota, Utah, Wisconsin,
          Wyoming) publish no fixed statewide number and leave the cancellation call to local school
          districts &mdash; this dashboard reports that explicitly rather than inventing a threshold
          for them. DC has no published high school association heat policy and falls back to the
          same advisory framing used elsewhere on this dashboard when a jurisdiction has no
          researched rule of its own. Wildfire smoke: the same{' '}
          <a
            href="https://assets.nfhs.org/umbraco/media/7212236/nfhs-position-statement-on-air-quality-april-2023-final.pdf"
            target="_blank"
            rel="noreferrer"
          >
            NFHS Position Statement on Air Quality
          </a>{' '}
          (2023) cited under Sports &amp; Athletics (NCAA) above &mdash; NFHS is the national
          governing body for high school athletics specifically, so this citation applies directly
          here rather than by analogy. Lightning: the same 30/30 rule used everywhere else on this
          dashboard. Cold weather: a regional band table supplied by the user (2026-08-06), a
          meaningfully different kind of source than the heat table above &mdash; it gives a typical
          threshold <em>range</em> for five broad regions (Northern, Midwest, Mid-Atlantic/Northeast,
          Southern, Mountain/West) plus a handful of named example states per region, not a number
          for all 50 states. This dashboard extends those examples to every state using ordinary
          geographic/climate judgment, not sourced per-state data &mdash; each ZIP&rsquo;s detail
          panel still names the real governing body, but the cold-weather number itself is a
          regional estimate, not that body&rsquo;s own published figure. Mountain/West states have no
          numeric range in the source at all (&ldquo;variable / district discretion&rdquo;) and are
          reported as such below freezing, rather than assigned an invented number.
        </p>
      )}

      {agriculture && (
        <>
          <p>
            Most states regulate agricultural and construction heat exposure under the same rule
            (California&rsquo;s 8 CCR &sect;3395 lists both as "high-heat industries" at the same
            80&deg;F/95&deg;F triggers; Maryland&rsquo;s COMAR 09.12.32 and Nevada&rsquo;s NAC 618
            apply to all industries with no carve-out) &mdash; this dashboard reuses those states&rsquo;
            construction module directly rather than duplicating it. Washington and Oregon give
            agriculture its own chapter for the identical numbers (WAC 296-307-097 vs. 296-62-095;
            OAR 437-004-1131 vs. 437-002-0156) &mdash; same requirements, different citation.
            Nevada&rsquo;s NAC 618 additionally requires agricultural employers specifically to
            provide actual shade for rest breaks, where non-agricultural employers may substitute
            an equally effective alternative like fans or misting.
          </p>
          <p>
            Cold weather: the same federal wind-chill rule construction uses (see Sources below)
            &mdash; no state or federal standard treats agricultural cold-stress exposure any
            differently, so this dashboard reuses that module unchanged rather than duplicating it.
          </p>
          <p>
            Colorado is the one state with a genuinely separate agriculture-only rule:{' '}
            <a href="https://www.law.cornell.edu/regulations/colorado/7-CCR-1103-15-3" target="_blank" rel="noreferrer">
              7 CCR 1103-15-3
            </a>{' '}
            (Agricultural Labor Conditions Rules, effective 2022) triggers at 80&deg;F with an
            increased-risk tier at 95&deg;F &mdash; construction has no Colorado-specific heat rule
            at all until HB 26-1272&rsquo;s numeric model plan, due 2028. There&rsquo;s no wind or
            stormwater category here: no state regulates farm-equipment wind limits the way CA
            regulates tower cranes, and agricultural stormwater discharges are exempt from NPDES
            permitting entirely under the Clean Water Act,{' '}
            <a href="https://www.law.cornell.edu/uscode/text/33/1362" target="_blank" rel="noreferrer">
              33 USC &sect;1362(14)
            </a>
            .
          </p>
        </>
      )}

      {isEnergy && (
        <>
          <p>
            Sourced from a 50-state energy-sector weather/labor compliance spreadsheet supplied
            by the user (2026-08-06), covering Wind, Solar, Oil &amp; Gas, and Hydropower. Heat
            and wildfire smoke text in that source is identical, state for state, to this
            dashboard&rsquo;s existing construction numbers &mdash; <strong>California</strong>{' '}
            (8 CCR &sect;3395 heat, &sect;5141.1 smoke), <strong>Washington</strong> (WAC
            296-62-095 heat, WAC 296-62-085 smoke), <strong>Oregon</strong> (OAR 437-002-0156
            heat, OAR 437-002-1081 smoke), <strong>Maryland</strong> (COMAR 09.12.32 heat), and{' '}
            <strong>Nevada</strong> (NAC 618 / R131-24AP heat) &mdash; so this dashboard reuses
            those modules directly rather than duplicating them. <strong>Colorado</strong>&rsquo;s
            only real state heat rule is agriculture-only (7 CCR 1103-15-3), and the source
            spreadsheet explicitly found no broader energy-sector standard there, so unlike
            Agriculture above, Colorado gets the federal fallback here &mdash; the same as every
            other unresearched state.
          </p>
          <p>
            Wind: <strong>California</strong> reuses the same personnel-hoist/tower-crane rule (8
            CCR &sect;1616.6 &amp; &sect;4966) as construction &mdash; the statute governs the
            equipment, not the industry using it, and turbine, panel, and rig erection commonly
            use cranes and personnel hoists too. Every other state runs the same federal fallback
            shape as construction (29 CFR 1926.1417 sets no fixed mph limit), with sector-specific
            framework citations: <strong>Wind Energy</strong> and <strong>Hydropower</strong> cite
            29 CFR 1910.269 (electric-power generation/O&amp;M) and 1926 Subpart V (construction);{' '}
            <strong>Solar Energy</strong> cites OSHA&rsquo;s general falls/cranes/electrical
            hazard guidance; <strong>Oil &amp; Gas</strong> cites{' '}
            <a
              href="https://www.osha.gov/etools/oil-and-gas/general-safety/site-conditions"
              target="_blank"
              rel="noreferrer"
            >
              OSHA&rsquo;s Oil &amp; Gas eTool
            </a>
            , which recommends site-specific weather stop-work triggers rather than a fixed
            number. Cold weather reuses the same federal wind-chill module as construction (see
            above) &mdash; nothing in the source distinguishes energy-sector cold-stress exposure.
            No stormwater category: the source spreadsheet doesn&rsquo;t cover NPDES/stormwater
            for any of the four sectors, so this dashboard doesn&rsquo;t invent one.
          </p>
        </>
      )}

      {!sports && !sportsHs && !agriculture && !isEnergy && (
        <>
          <p>
            California:{' '}
            <a href="https://www.dir.ca.gov/title8/3395.html" target="_blank" rel="noreferrer">
              8 CCR &sect;3395
            </a>{' '}
            (heat) &middot;{' '}
            <a href="https://www.dir.ca.gov/title8/5141_1.html" target="_blank" rel="noreferrer">
              &sect;5141.1
            </a>{' '}
            (wildfire smoke) &middot;{' '}
            <a href="https://www.dir.ca.gov/title8/1616.6.html" target="_blank" rel="noreferrer">
              &sect;1616.6
            </a>{' '}
            &amp;{' '}
            <a href="https://www.dir.ca.gov/title8/4966.html" target="_blank" rel="noreferrer">
              &sect;4966
            </a>{' '}
            (wind) &middot;{' '}
            <a
              href="https://www.waterboards.ca.gov/water_issues/programs/stormwater/construction/cgp-faq.html"
              target="_blank"
              rel="noreferrer"
            >
              2022 CGP
            </a>{' '}
            (stormwater).
          </p>
          <p>
            Washington:{' '}
            <a href="https://apps.leg.wa.gov/WAC/default.aspx?cite=296-62-095" target="_blank" rel="noreferrer">
              WAC 296-62-095
            </a>{' '}
            (heat) &middot;{' '}
            <a href="https://apps.leg.wa.gov/WAC/default.aspx?cite=296-62-085" target="_blank" rel="noreferrer">
              WAC 296-62-085
            </a>{' '}
            (wildfire smoke). Oregon:{' '}
            <a href="https://osha.oregon.gov/OSHAPubs/factsheets/fs91.pdf" target="_blank" rel="noreferrer">
              OAR 437-002-0156
            </a>{' '}
            (heat) &middot;{' '}
            <a href="https://osha.oregon.gov/OSHAPubs/factsheets/fs92.pdf" target="_blank" rel="noreferrer">
              OAR 437-002-1081
            </a>{' '}
            (wildfire smoke). Maryland:{' '}
            <a href="https://labor.maryland.gov/labor/mosh/moshheatstress.shtml" target="_blank" rel="noreferrer">
              COMAR 09.12.32
            </a>{' '}
            (heat). Nevada:{' '}
            <a href="https://dir.nv.gov/OSHA/Home/" target="_blank" rel="noreferrer">
              NAC 618 (R131-24AP)
            </a>{' '}
            (heat). Everywhere else: the federal General Duty Clause,{' '}
            <a href="https://www.osha.gov/laws-regs/oshact/section5-duties" target="_blank" rel="noreferrer">
              29 USC &sect;654(a)(1)
            </a>
            , advisory only &mdash; no fixed federal numeric standard exists yet for heat or wildfire
            smoke, and 29 CFR 1926.1417 sets no fixed wind speed limit anywhere.
          </p>
          <p>
            Stormwater for the other 49 states: researched from{' '}
            <a href="https://regs.stormwaterworld.com/" target="_blank" rel="noreferrer">
              regs.stormwaterworld.com
            </a>
            , a third-party permit aggregator &mdash; verify against the state&rsquo;s own NPDES
            permit before relying on it for a real compliance decision. DC is not yet researched.
          </p>
        </>
      )}
      <p style={{ marginTop: 12 }}>
        This dashboard is an operational planning aid, not legal advice or a compliance record.
        Each rule above names its own citation and enforcing agency &mdash; that agency enforces
        against on-site conditions and your written prevention plan, not a forecast.
      </p>
    </div>
  );
}
